import axios, { AxiosInstance, AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';

// API base URL - use relative path to work with ngrok and any domain
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api/v1';

// Default timeout for API requests (30 seconds)
const DEFAULT_TIMEOUT = 30000;

// Timeout for file upload/download operations (2 minutes)
const FILE_OPERATION_TIMEOUT = 120000;

// Token refresh state — shared across both clients
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}> = [];

function drainQueue(error: unknown, token: string | null = null) {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token!);
    }
  });
  failedQueue = [];
}

function setupInterceptors(client: AxiosInstance): void {
  // Request interceptor: inject auth token and adjust timeout for file ops
  client.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const token = localStorage.getItem('access_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      if (
        config.url?.includes('/documents') ||
        config.url?.includes('/export') ||
        config.url?.includes('/import') ||
        config.headers?.['Content-Type'] === 'multipart/form-data'
      ) {
        config.timeout = FILE_OPERATION_TIMEOUT;
      }

      return config;
    },
    (error) => Promise.reject(error)
  );

  // Response interceptor: retry once with refreshed token on 401
  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

      // Handle timeout errors
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        error.message = 'การเชื่อมต่อหมดเวลา กรุณาลองใหม่อีกครั้ง';
        return Promise.reject(error);
      }

      // Handle network errors
      if (!error.response && error.message === 'Network Error') {
        error.message = 'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้ กรุณาตรวจสอบการเชื่อมต่ออินเทอร์เน็ต';
        return Promise.reject(error);
      }

      if (
        error.response?.status === 401 &&
        !originalRequest._retry &&
        window.location.pathname !== '/login' &&
        // Don't retry refresh calls themselves to avoid infinite loops
        !originalRequest.url?.includes('/auth/refresh')
      ) {
        if (isRefreshing) {
          // Queue this request until the ongoing refresh completes
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          }).then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return client(originalRequest);
          });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const response = await axios.post(
            `${API_BASE_URL}/auth/refresh`,
            {},
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem('access_token')}`,
              },
            }
          );
          const newToken: string = response.data.access_token;
          localStorage.setItem('access_token', newToken);
          if (response.data.user) {
            localStorage.setItem('user', JSON.stringify(response.data.user));
          }
          drainQueue(null, newToken);
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return client(originalRequest);
        } catch (refreshError) {
          drainQueue(refreshError, null);
          localStorage.removeItem('access_token');
          localStorage.removeItem('user');
          window.location.href = '/login?reason=expired';
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      return Promise.reject(error);
    }
  );
}

// Create axios instance with default configuration
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: DEFAULT_TIMEOUT,
});

setupInterceptors(apiClient);

/**
 * Create a custom axios instance with specific timeout.
 * Shares the same auth/refresh interceptor logic as the default client.
 */
export function createApiClientWithTimeout(timeout: number): AxiosInstance {
  const client = axios.create({
    baseURL: API_BASE_URL,
    headers: {
      'Content-Type': 'application/json',
    },
    timeout,
  });

  setupInterceptors(client);
  return client;
}

export default apiClient;
